import { useState, useEffect, useCallback, useRef } from 'react';
import { hrms } from '../../services/api';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import { theme } from '../../theme';
import { SearchableSelect } from '../../components/common/SearchableSelect';
import { Fingerprint, PenLine } from 'lucide-react';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_ABBR = ['Su','Mo','Tu','We','Th','Fr','Sa'];
const YEARS = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

const HOLIDAY_TYPE_COLORS = {
  public:     { bg: '#fef3c7', color: '#d97706', label: 'Public' },
  optional:   { bg: '#dbeafe', color: '#2563eb', label: 'Optional' },
  restricted: { bg: '#ede9fe', color: '#7c3aed', label: 'Restricted' },
};

const STATUS_CONFIG = {
  present:   { code: 'P',   color: '#16a34a', bg: '#dcfce7' },
  absent:    { code: 'A',   color: '#dc2626', bg: '#fee2e2' },
  on_leave:  { code: 'L',   color: '#d97706', bg: '#fef3c7' },
  wfh:       { code: 'WFH', color: '#2563eb', bg: '#dbeafe' },
  half_day:  { code: 'H',   color: '#7c3aed', bg: '#ede9fe' },
  late:      { code: 'LT',  color: '#ea580c', bg: '#ffedd5' },
  week_off:  { code: 'WO',  color: '#0891b2', bg: '#e0f2fe' },
};

const STATUS_OPTIONS = [
  { value: 'present',  label: 'Present (P)' },
  { value: 'absent',   label: 'Absent (A)' },
  { value: 'on_leave', label: 'Leave (L)' },
  { value: 'wfh',      label: 'WFH' },
  { value: 'half_day', label: 'Half Day (H)' },
  { value: 'late',     label: 'Late (LT)' },
];

const EMPTY_HOLIDAY = { date: '', name: '', holiday_type: 'public', description: '' };

function getDaysInMonth(year, month) { return new Date(year, month, 0).getDate(); }
function getDayOfWeek(year, month, day) { return new Date(year, month - 1, day).getDay(); }
function isWeekend(year, month, day) { const d = getDayOfWeek(year, month, day); return d === 0 || d === 6; }

// ─── Holidays Tab ─────────────────────────────────────────────────────────────
function HolidaysTab({ year }) {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_HOLIDAY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    hrms.getHolidays(year)
      .then(r => setHolidays(r.data || []))
      .catch(() => setHolidays([]))
      .finally(() => setLoading(false));
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm(EMPTY_HOLIDAY); setModal(true); };
  const openEdit = (h) => {
    setEditing(h);
    setForm({ date: h.date, name: h.name, holiday_type: h.holiday_type, description: h.description || '' });
    setModal(true);
  };

  const handleSave = async () => {
    if (!form.date || !form.name.trim()) { alert('Date and name are required.'); return; }
    setSaving(true);
    try {
      if (editing) {
        await hrms.updateHoliday(editing.id, { name: form.name, holiday_type: form.holiday_type, description: form.description });
      } else {
        await hrms.createHoliday(form);
      }
      setModal(false);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to save holiday');
    } finally { setSaving(false); }
  };

  const handleDelete = async (h) => {
    if (!window.confirm(`Delete "${h.name}" on ${h.date}?`)) return;
    try { await hrms.deleteHoliday(h.id); load(); }
    catch (e) { alert(e.response?.data?.detail || 'Failed to delete'); }
  };

  const inp = { width: '100%', padding: '0.6rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none', boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.text, marginBottom: '0.35rem' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: theme.text, margin: 0 }}>Holidays — {year}</h2>
          <p style={{ color: theme.textMuted, fontSize: '0.8rem', margin: '0.2rem 0 0' }}>
            {holidays.length} holiday{holidays.length !== 1 ? 's' : ''} configured · Highlighted in the attendance sheet
          </p>
        </div>
        <Button onClick={openAdd}>+ Add Holiday</Button>
      </div>

      {loading ? (
        <p style={{ color: theme.textMuted }}>Loading…</p>
      ) : holidays.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem 1rem', color: theme.textMuted, background: '#f9fafb', borderRadius: '0.75rem', border: `1px dashed ${theme.border}` }}>
          No holidays added for {year}. Click "+ Add Holiday" to get started.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {holidays.map(h => {
            const cfg = HOLIDAY_TYPE_COLORS[h.holiday_type] || HOLIDAY_TYPE_COLORS.public;
            const dow = new Date(h.date).toLocaleDateString('en-IN', { weekday: 'long' });
            return (
              <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: '1rem', background: '#fff', border: `1px solid ${theme.border}`, borderLeft: `4px solid ${cfg.color}`, borderRadius: '0.5rem', padding: '0.75rem 1rem' }}>
                <div style={{ minWidth: 90 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', color: theme.text }}>{new Date(h.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</div>
                  <div style={{ fontSize: '0.72rem', color: theme.textMuted }}>{dow}</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.875rem', color: theme.text }}>{h.name}</div>
                  {h.description && <div style={{ fontSize: '0.75rem', color: theme.textMuted }}>{h.description}</div>}
                </div>
                <span style={{ padding: '0.2rem 0.6rem', borderRadius: '1rem', background: cfg.bg, color: cfg.color, fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {cfg.label}
                </span>
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  <button onClick={() => openEdit(h)} style={{ padding: '0.3rem 0.6rem', border: `1px solid ${theme.border}`, borderRadius: '0.375rem', background: '#fff', cursor: 'pointer', fontSize: '0.75rem', color: theme.text }}>Edit</button>
                  <button onClick={() => handleDelete(h)} style={{ padding: '0.3rem 0.6rem', border: `1px solid #fca5a5`, borderRadius: '0.375rem', background: '#fff', cursor: 'pointer', fontSize: '0.75rem', color: '#dc2626' }}>Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal isOpen={modal} onClose={() => setModal(false)} title={editing ? 'Edit Holiday' : 'Add Holiday'}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
          <div>
            <label style={lbl}>Date *</label>
            <input style={{ ...inp, background: editing ? '#f3f4f6' : '#fff' }} type="date" value={form.date}
              onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
              disabled={!!editing} />
          </div>
          <div>
            <label style={lbl}>Holiday Name *</label>
            <input style={inp} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Diwali" />
          </div>
          <div>
            <label style={lbl}>Type</label>
            <select style={inp} value={form.holiday_type} onChange={e => setForm(f => ({ ...f, holiday_type: e.target.value }))}>
              <option value="public">Public Holiday</option>
              <option value="optional">Optional Holiday</option>
              <option value="restricted">Restricted Holiday</option>
            </select>
          </div>
          <div>
            <label style={lbl}>Description (optional)</label>
            <input style={inp} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Brief description..." />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
            <Button variant="secondary" onClick={() => setModal(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>Save Holiday</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ─── Attendance Sheet ──────────────────────────────────────────────────────────
function AttendanceManagement() {
  const [activeTab, setActiveTab] = useState('sheet');
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [records, setRecords] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [deptFilter, setDeptFilter] = useState('');
  const [empFilter, setEmpFilter] = useState('');
  const [cellModal, setCellModal] = useState(null);
  const [cellStatus, setCellStatus] = useState('present');
  const [cellNotes, setCellNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const scrollRef = useRef(null);

  const daysInMonth = getDaysInMonth(year, month);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  useEffect(() => {
    hrms.getEmployees({ limit: 500 }).then(r => {
      const d = r.data;
      setEmployees(Array.isArray(d) ? d : d.items || []);
    }).catch(err => console.warn('[HRMS]', err?.message));
    hrms.getDepartments().then(r => setDepartments(r.data || [])).catch(err => console.warn('[HRMS]', err?.message));
  }, []);

  const fetchAttendance = useCallback(async () => {
    setLoading(true);
    try {
      const [attRes, holRes] = await Promise.all([
        hrms.getAttendance({ month, year }),
        hrms.getHolidays(year),
      ]);
      const d = attRes.data;
      setRecords(Array.isArray(d) ? d : d.items || []);
      setHolidays(holRes.data || []);
    } catch { setRecords([]); }
    finally { setLoading(false); }
  }, [month, year]);

  useEffect(() => { fetchAttendance(); }, [fetchAttendance]);

  // Build holiday set for fast lookup: "YYYY-MM-DD" -> holiday object
  const holidayMap = {};
  holidays.forEach(h => { holidayMap[h.date] = h; });

  function isHoliday(day) {
    const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    return holidayMap[dateStr] || null;
  }

  const filteredEmployees = employees
    .filter(e => !deptFilter || String(e.department_id) === deptFilter)
    .filter(e => !empFilter || String(e.id) === empFilter);

  const recordMap = {};
  records.forEach(r => {
    const day = new Date(r.date).getDate();
    if (!recordMap[r.employee_id]) recordMap[r.employee_id] = {};
    recordMap[r.employee_id][day] = r;
  });

  // For flexible_week_off employees: per ISO week, the first unrecorded non-holiday
  // day is the week off. All remaining unrecorded days are absent.
  function getWeekOffDays(empId, attendanceType) {
    if (attendanceType !== 'flexible_week_off') return new Set();
    const empRecs = recordMap[empId] || {};
    const weekOffSet = new Set();
    // Group days in this month by Mon-anchored week key
    const weekGroups = {};
    days.forEach(d => {
      const date = new Date(year, month - 1, d);
      const dow = date.getDay(); // 0=Sun
      // Shift so Monday=0: (dow+6)%7
      const mondayOffset = (dow + 6) % 7;
      const monday = new Date(date);
      monday.setDate(date.getDate() - mondayOffset);
      const key = monday.toISOString().slice(0, 10);
      if (!weekGroups[key]) weekGroups[key] = [];
      weekGroups[key].push(d);
    });
    Object.values(weekGroups).forEach(weekDays => {
      const missing = weekDays.filter(d => {
        if (isHoliday(d)) return false;
        const rec = empRecs[d];
        if (!rec) return true;
        // marked absent with no check-in also counts as "missing"
        return rec.status === 'absent';
      });
      if (missing.length >= 1) weekOffSet.add(missing[0]);
    });
    return weekOffSet;
  }

  function getTotals(empId, attendanceType) {
    const empRecs = recordMap[empId] || {};
    const weekOffDays = getWeekOffDays(empId, attendanceType);
    const isFlexible = attendanceType === 'flexible_week_off';
    let present = 0, leave = 0, absent = 0, wfh = 0, weekOff = 0;
    days.forEach(d => {
      if (isHoliday(d)) return;
      if (!isFlexible && isWeekend(year, month, d)) return;
      if (weekOffDays.has(d)) { weekOff++; return; }
      const rec = empRecs[d];
      if (!rec) { absent++; return; }
      const s = rec.status;
      if (s === 'present' || s === 'late') present++;
      else if (s === 'on_leave') leave++;
      else if (s === 'absent') absent++;
      else if (s === 'wfh') wfh++;
    });
    return { present, leave, absent, wfh, weekOff };
  }

  function openCell(emp, day) {
    const holiday = isHoliday(day);
    if (holiday) return;
    const rec = (recordMap[emp.id] || {})[day];
    // Virtual records from approved leave/WFH are read-only
    if (rec?.source === 'leave' || rec?.source === 'wfh') return;
    setCellModal({ emp, day, record: rec });
    setCellStatus(rec?.status || 'present');
    setCellNotes(rec?.notes || '');
  }

  async function handleCellSave() {
    setSaving(true);
    try {
      const rec = cellModal.record;
      const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(cellModal.day).padStart(2,'0')}`;
      if (rec) {
        await hrms.editAttendance(rec.id, { status: cellStatus, notes: cellNotes });
      } else {
        await hrms.markAttendance({ employee_id: cellModal.emp.id, date: dateStr, status: cellStatus, notes: cellNotes });
      }
      setCellModal(null);
      fetchAttendance();
    } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  }

  async function handleCellClear() {
    if (!cellModal?.record) { setCellModal(null); return; }
    if (!window.confirm('Clear this attendance record?')) return;
    setSaving(true);
    try {
      await hrms.deleteAttendance(cellModal.record.id);
      setCellModal(null);
      fetchAttendance();
    } catch (e) { alert('Failed to clear: ' + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  }

  function renderCell(emp, day) {
    const isFlexible = emp.attendance_type === 'flexible_week_off';
    const weekend = isWeekend(year, month, day);
    const holiday = isHoliday(day);
    const rec = (recordMap[emp.id] || {})[day];
    const weekOffDays = getWeekOffDays(emp.id, emp.attendance_type);
    const isWeekOff = weekOffDays.has(day);

    // Week off cell (flexible employees only)
    if (isWeekOff) {
      return (
        <td key={day} title="Week Off" style={{ width: 38, minWidth: 38, height: 36, textAlign: 'center', verticalAlign: 'middle', borderRight: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', background: '#e0f2fe', cursor: 'default', fontSize: '0.7rem', fontWeight: 700, color: '#0891b2', userSelect: 'none' }}>
          WO
        </td>
      );
    }

    // Fixed weekend for general employees
    if (!isFlexible && weekend) {
      return (
        <td key={day} style={{ width: 38, minWidth: 38, height: 36, textAlign: 'center', verticalAlign: 'middle', borderRight: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', background: '#f3f4f6', cursor: 'default', fontSize: '0.7rem', fontWeight: 700, color: '#9ca3af', userSelect: 'none' }}>
          –
        </td>
      );
    }

    // For flexible employees on weekends with a record — fall through to normal render
    // For flexible employees on weekends with no record — show as absent (already handled below)
    const cfg = rec ? STATUS_CONFIG[rec.status] : null;
    if (holiday) {
      const hcfg = HOLIDAY_TYPE_COLORS[holiday.holiday_type] || HOLIDAY_TYPE_COLORS.public;
      return (
        <td key={day} title={holiday.name} style={{ width: 38, minWidth: 38, height: 36, textAlign: 'center', verticalAlign: 'middle', borderRight: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', background: hcfg.bg, cursor: 'default', fontSize: '0.6rem', fontWeight: 700, color: hcfg.color, userSelect: 'none' }}>
          H
        </td>
      );
    }
    const isVirtual = rec?.source === 'leave' || rec?.source === 'wfh';
    const isPresent = rec && ['present', 'late', 'half_day'].includes(rec.status);
    const src = isPresent && !isVirtual ? (rec.source || 'manual') : null;
    const approvalStatus = rec?.approval_status;
    const isPendingManager = rec?.source === 'manual' && approvalStatus === 'pending_manager';
    const isPendingHR      = rec?.source === 'manual' && approvalStatus === 'pending_hr';
    const isPendingManual  = isPendingManager || isPendingHR;
    const SOURCE_LABEL = { manual: 'Manual Check-in', biometric: 'Biometric Check-in', both: 'Biometric + Manual' };
    const pendingLabel = isPendingManager ? 'Pending Manager Approval' : 'Pending HR Approval';
    const title = isVirtual
      ? (rec.source === 'leave' ? 'Approved Leave (auto)' : 'Approved WFH (auto)')
      : isPendingManual
        ? `${rec.status} · Manual (${pendingLabel})`
        : rec
          ? `${rec.status}${src ? ' · ' + SOURCE_LABEL[src] : ''}`
          : 'Not marked — click to mark';
    const cellBg    = isPendingManager ? '#FFFBEB' : isPendingHR ? '#EFF6FF' : cfg ? cfg.bg : '#fff';
    const cellColor = isPendingManager ? '#B45309' : isPendingHR ? '#1D4ED8' : cfg ? cfg.color : undefined;
    const cellBorder = isPendingManager ? '#FDE68A' : isPendingHR ? '#BFDBFE' : '#e5e7eb';
    const penColor  = isPendingManager ? '#B45309' : isPendingHR ? '#3B82F6' : '#6B7280';
    return (
      <td key={day} onClick={() => openCell(emp, day)}
        style={{ width: 38, minWidth: 38, height: 36, textAlign: 'center', verticalAlign: 'middle', borderRight: '1px solid #e5e7eb', borderBottom: `1px solid ${cellBorder}`, background: cellBg, cursor: isVirtual ? 'default' : 'pointer', userSelect: 'none', transition: 'opacity 0.1s', opacity: isVirtual ? 0.85 : 1 }}
        title={title}>
        {cfg ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1 }}>
            <span style={{ fontSize: '0.7rem', fontWeight: 700, color: cellColor, lineHeight: 1 }}>{cfg.code}</span>
            {src && (
              <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                {(src === 'biometric' || src === 'both') && (
                  <Fingerprint size={8} strokeWidth={2} color="#6B7280" />
                )}
                {(src === 'manual' || src === 'both') && (
                  <PenLine size={8} strokeWidth={2} color={penColor} />
                )}
              </div>
            )}
          </div>
        ) : ''}
      </td>
    );
  }

  function renderDayHeader(d) {
    const dow = getDayOfWeek(year, month, d);
    const weekend = dow === 0 || dow === 6;
    const holiday = isHoliday(d);
    const hcfg = holiday ? (HOLIDAY_TYPE_COLORS[holiday.holiday_type] || HOLIDAY_TYPE_COLORS.public) : null;
    return (
      <th key={`dow-${d}`} title={holiday ? holiday.name : undefined}
        style={{ width: 38, minWidth: 38, textAlign: 'center', padding: '0.25rem 0', borderRight: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', fontSize: '0.65rem', color: holiday ? hcfg.color : (weekend ? '#9ca3af' : theme.textMuted), background: holiday ? hcfg.bg : (weekend ? '#f3f4f6' : '#f9fafb'), fontWeight: 600, position: 'sticky', top: 0, zIndex: 2 }}>
        {DAY_ABBR[dow]}
      </th>
    );
  }

  function renderDateHeader(d) {
    const weekend = isWeekend(year, month, d);
    const holiday = isHoliday(d);
    const hcfg = holiday ? (HOLIDAY_TYPE_COLORS[holiday.holiday_type] || HOLIDAY_TYPE_COLORS.public) : null;
    return (
      <th key={`date-${d}`} title={holiday ? holiday.name : undefined}
        style={{ width: 38, minWidth: 38, textAlign: 'center', padding: '0.25rem 0', borderRight: '1px solid #e5e7eb', borderBottom: '2px solid #d1d5db', fontSize: '0.7rem', fontWeight: 700, color: holiday ? hcfg.color : (weekend ? '#9ca3af' : theme.text), background: holiday ? hcfg.bg : (weekend ? '#f3f4f6' : '#f9fafb'), position: 'sticky', top: 36, zIndex: 2 }}>
        {d}
      </th>
    );
  }

  const cellStyle = { padding: '0.4rem 0.6rem', border: '1px solid #e5e7eb', fontSize: '0.75rem', color: theme.text, whiteSpace: 'nowrap', verticalAlign: 'middle' };
  const inp = { width: '100%', padding: '0.6rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none', marginBottom: '0.75rem', boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.text, marginBottom: '0.35rem' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

      {/* Page title */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>Attendance Management</h1>
          <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>Monthly attendance sheet and holiday calendar</p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: `2px solid ${theme.border}`, gap: 4 }}>
        {[{ id: 'sheet', label: 'Attendance Sheet' }, { id: 'holidays', label: 'Holidays' }].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{ padding: '0.5rem 1.25rem', border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600, color: activeTab === t.id ? theme.primary : theme.textMuted, borderBottom: activeTab === t.id ? `2px solid ${theme.primary}` : '2px solid transparent', marginBottom: -2 }}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'holidays' ? (
        <HolidaysTab year={year} />
      ) : (
        <>
          {/* Controls */}
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', background: '#fff', padding: '0.875rem 1rem', borderRadius: '0.75rem', border: `1px solid ${theme.border}` }}>
            <select value={month} onChange={e => setMonth(Number(e.target.value))}
              style={{ padding: '0.5rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none' }}>
              {MONTHS.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
            </select>
            <select value={year} onChange={e => setYear(Number(e.target.value))}
              style={{ padding: '0.5rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none' }}>
              {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)}
              style={{ padding: '0.5rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none', minWidth: 160 }}>
              <option value="">All Departments</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <SearchableSelect
              options={employees
                .filter(e => !deptFilter || String(e.department_id) === deptFilter)
                .map(e => ({ value: e.id, label: e.full_name, meta: e.emp_code }))}
              value={empFilter}
              onChange={v => setEmpFilter(v ? String(v) : '')}
              placeholder="All Employees"
              style={{ minWidth: 200 }}
            />
            {holidays.filter(h => {
              const m = new Date(h.date).getMonth() + 1;
              return m === month;
            }).length > 0 && (
              <span style={{ fontSize: '0.78rem', color: '#d97706', background: '#fef3c7', padding: '0.3rem 0.6rem', borderRadius: '0.375rem', fontWeight: 600 }}>
                {holidays.filter(h => new Date(h.date).getMonth() + 1 === month).length} holiday(s) this month
              </span>
            )}
            <span style={{ fontSize: '0.8rem', color: theme.textMuted, marginLeft: 'auto' }}>
              {filteredEmployees.length} employees
            </span>
          </div>

          {/* Sheet */}
          <div style={{ background: '#fff', borderRadius: '0.75rem', border: `1px solid ${theme.border}`, overflow: 'hidden' }}>
            <div style={{ padding: '1rem 1.5rem', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, fontSize: '1rem', color: theme.text }}>Monthly Employee Attendance Sheet</span>
              <span style={{ fontSize: '0.875rem', color: theme.textMuted }}>{MONTHS[month - 1]} {year}</span>
            </div>

            {loading ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: theme.textMuted }}>Loading...</div>
            ) : (
              <div ref={scrollRef} style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed', fontSize: '0.75rem', minWidth: '100%' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb' }}>
                      <th style={{ ...cellStyle, width: 160, minWidth: 160, fontWeight: 700, textAlign: 'left', position: 'sticky', left: 0, top: 0, background: '#f9fafb', zIndex: 4 }}>Employee</th>
                      {days.map(d => renderDayHeader(d))}
                      <th style={{ ...cellStyle, width: 52, minWidth: 52, textAlign: 'center', background: '#fef9f0', color: '#16a34a', fontWeight: 700, position: 'sticky', right: 208, top: 0, zIndex: 4 }}>P</th>
                      <th style={{ ...cellStyle, width: 52, minWidth: 52, textAlign: 'center', background: '#fef9f0', color: '#d97706', fontWeight: 700, position: 'sticky', right: 156, top: 0, zIndex: 4 }}>L</th>
                      <th style={{ ...cellStyle, width: 52, minWidth: 52, textAlign: 'center', background: '#fef9f0', color: '#dc2626', fontWeight: 700, position: 'sticky', right: 104, top: 0, zIndex: 4 }}>A</th>
                      <th style={{ ...cellStyle, width: 52, minWidth: 52, textAlign: 'center', background: '#fef9f0', color: '#2563eb', fontWeight: 700, position: 'sticky', right: 52, top: 0, zIndex: 4 }}>WFH</th>
                      <th style={{ ...cellStyle, width: 52, minWidth: 52, textAlign: 'center', background: '#fef9f0', color: '#0891b2', fontWeight: 700, position: 'sticky', right: 0, top: 0, zIndex: 4 }}>WO</th>
                    </tr>
                    <tr style={{ background: '#f9fafb' }}>
                      <th style={{ ...cellStyle, position: 'sticky', left: 0, top: 36, background: '#f9fafb', zIndex: 4, textAlign: 'left', fontSize: '0.7rem', color: theme.textMuted }}>Date →</th>
                      {days.map(d => renderDateHeader(d))}
                      <th colSpan={5} style={{ background: '#fef9f0', borderBottom: '2px solid #d1d5db', textAlign: 'center', fontSize: '0.7rem', color: theme.textMuted, fontWeight: 600, padding: '0.25rem', position: 'sticky', right: 0, top: 36, zIndex: 4, width: 260, minWidth: 260 }}>TOTALS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmployees.length === 0 ? (
                      <tr><td colSpan={days.length + 6} style={{ padding: '2rem', textAlign: 'center', color: theme.textMuted }}>No employees found</td></tr>
                    ) : filteredEmployees.map((emp, idx) => {
                      const totals = getTotals(emp.id, emp.attendance_type);
                      const rowBg = idx % 2 === 0 ? '#fff' : '#fafafa';
                      return (
                        <tr key={emp.id} style={{ background: rowBg }}>
                          <td style={{ ...cellStyle, position: 'sticky', left: 0, background: rowBg, zIndex: 1, fontWeight: 600, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            <div style={{ fontWeight: 600, color: theme.text, fontSize: '0.78rem' }}>{emp.full_name}</div>
                            <div style={{ fontSize: '0.65rem', color: theme.textMuted }}>{emp.emp_code}</div>
                            {emp.attendance_type === 'flexible_week_off' && (
                              <div style={{ fontSize: '0.6rem', color: '#0891b2', fontWeight: 600 }}>Flex WO</div>
                            )}
                          </td>
                          {days.map(d => renderCell(emp, d))}
                          <td style={{ textAlign: 'center', fontWeight: 700, color: '#16a34a', background: '#f0fdf4', borderRight: '1px solid #e5e7eb', fontSize: '0.8rem', padding: '0.25rem', position: 'sticky', right: 208, zIndex: 1, width: 52, minWidth: 52 }}>{totals.present}</td>
                          <td style={{ textAlign: 'center', fontWeight: 700, color: '#d97706', background: '#fffbeb', borderRight: '1px solid #e5e7eb', fontSize: '0.8rem', padding: '0.25rem', position: 'sticky', right: 156, zIndex: 1, width: 52, minWidth: 52 }}>{totals.leave}</td>
                          <td style={{ textAlign: 'center', fontWeight: 700, color: '#dc2626', background: '#fef2f2', borderRight: '1px solid #e5e7eb', fontSize: '0.8rem', padding: '0.25rem', position: 'sticky', right: 104, zIndex: 1, width: 52, minWidth: 52 }}>{totals.absent}</td>
                          <td style={{ textAlign: 'center', fontWeight: 700, color: '#2563eb', background: '#eff6ff', borderRight: '1px solid #e5e7eb', fontSize: '0.8rem', padding: '0.25rem', position: 'sticky', right: 52, zIndex: 1, width: 52, minWidth: 52 }}>{totals.wfh}</td>
                          <td style={{ textAlign: 'center', fontWeight: 700, color: '#0891b2', background: '#e0f2fe', fontSize: '0.8rem', padding: '0.25rem', position: 'sticky', right: 0, zIndex: 1, width: 52, minWidth: 52 }}>{totals.weekOff || 0}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Legend */}
            <div style={{ padding: '0.75rem 1.5rem', borderTop: `1px solid ${theme.border}`, display: 'flex', gap: '1.25rem', flexWrap: 'wrap', background: '#f9fafb', alignItems: 'center' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: theme.text }}>Legend:</span>
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <span key={key} style={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <span style={{ display: 'inline-block', width: 22, height: 18, background: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: '0.65rem', textAlign: 'center', lineHeight: '18px', borderRadius: 3 }}>{cfg.code}</span>
                  <span style={{ color: theme.textMuted }}>{key.replace('_', ' ')}</span>
                </span>
              ))}
              <span style={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                <span style={{ display: 'inline-block', width: 22, height: 18, background: '#f3f4f6', color: '#9ca3af', fontWeight: 700, fontSize: '0.65rem', textAlign: 'center', lineHeight: '18px', borderRadius: 3 }}>–</span>
                <span style={{ color: theme.textMuted }}>Weekend</span>
              </span>
              <span style={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                <span style={{ display: 'inline-block', width: 22, height: 18, background: '#e0f2fe', color: '#0891b2', fontWeight: 700, fontSize: '0.65rem', textAlign: 'center', lineHeight: '18px', borderRadius: 3 }}>WO</span>
                <span style={{ color: theme.textMuted }}>Week Off (Flex)</span>
              </span>
              <span style={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                <span style={{ display: 'inline-block', width: 22, height: 18, background: '#fef3c7', color: '#d97706', fontWeight: 700, fontSize: '0.65rem', textAlign: 'center', lineHeight: '18px', borderRadius: 3 }}>H</span>
                <span style={{ color: theme.textMuted }}>Holiday</span>
              </span>
              <span style={{ borderLeft: `1px solid ${theme.border}`, paddingLeft: '1rem', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{ fontWeight: 700, color: theme.text }}>Check-in:</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <Fingerprint size={12} strokeWidth={2} color="#6B7280" />
                  <span style={{ color: theme.textMuted }}>Biometric</span>
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <PenLine size={12} strokeWidth={2} color="#6B7280" />
                  <span style={{ color: theme.textMuted }}>Manual</span>
                </span>
              </span>
            </div>
          </div>
        </>
      )}

      {/* Cell Edit Modal */}
      <Modal
        isOpen={!!cellModal}
        onClose={() => setCellModal(null)}
        title={cellModal ? `${cellModal.emp.full_name} — ${MONTHS[month-1]} ${cellModal.day}, ${year}` : ''}
        footer={
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', width: '100%' }}>
            {cellModal?.record && <Button variant="outlineGray" onClick={handleCellClear} loading={saving}>Clear</Button>}
            <Button variant="outlineGray" onClick={() => setCellModal(null)}>Cancel</Button>
            <Button variant="primary" onClick={handleCellSave} loading={saving}>Save</Button>
          </div>
        }>
        {cellModal && (
          <>
            <label style={lbl}>Attendance Status</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '1rem' }}>
              {STATUS_OPTIONS.map(opt => {
                const cfg = STATUS_CONFIG[opt.value];
                const selected = cellStatus === opt.value;
                return (
                  <button key={opt.value} onClick={() => setCellStatus(opt.value)}
                    style={{ padding: '0.6rem', border: `2px solid ${selected ? cfg.color : theme.border}`, borderRadius: '0.5rem', background: selected ? cfg.bg : '#fff', color: selected ? cfg.color : theme.text, fontWeight: selected ? 700 : 400, cursor: 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <span style={{ fontWeight: 700 }}>{cfg.code}</span> — {opt.label.split(' (')[0]}
                  </button>
                );
              })}
            </div>
            <label style={lbl}>Notes (optional)</label>
            <input style={inp} value={cellNotes} onChange={e => setCellNotes(e.target.value)} placeholder="Add notes..." />
          </>
        )}
      </Modal>
    </div>
  );
}

export default AttendanceManagement;
